
import type { ShareData } from '../types/scenario.ts';
import { getLanguage, getLocale } from './i18n.ts';

const APP_URL = 'https://globdrop.vedratic.com';

const messages = {
  ca: {
    title: '🌍 Globdrop — Em superes?',
    flagTitle: '🚩 Globdrop Banderes — Em superes?',
    score: (pts: string, sc: string, m: string) => `He fet ${pts} punts jugant a ${sc} (mode ${m}).`,
    flagScore: (pts: string, sc: string, m: string) => `He fet ${pts} punts endevinant banderes a ${sc} (mode ${m}).`,
    level: (lvl: string) => `M'he convertit en tot un/a ${lvl}!`,
    km: (km: number) => `He recorregut ${km} km innecessaris 😅`,
    mi: (mi: number) => `He recorregut ${mi} milles innecessàries 😅`,
    flagCorrect: (correct: number, total: number) => `He encertat ${correct}/${total} banderes! 🎯`,
    try: 'Prova-ho tu:',
    tags: '#Globdrop #Geografia #Catalunya',
    flagTags: '#Globdrop #Banderes #Geografia'
  },
  es: {
    title: '🌍 Globdrop — ¿Me superas?',
    flagTitle: '🚩 Globdrop Banderas — ¿Me superas?',
    score: (pts: string, sc: string, m: string) => `He conseguido ${pts} puntos jugando a ${sc} (modo ${m}).`,
    flagScore: (pts: string, sc: string, m: string) => `He conseguido ${pts} puntos adivinando banderas en ${sc} (modo ${m}).`,
    level: (lvl: string) => `¡Me he convertido en un/a ${lvl}!`,
    km: (km: number) => `He recorrido ${km} km innecesarios 😅`,
    mi: (mi: number) => `He recorrido ${mi} millas innecesarias 😅`,
    flagCorrect: (correct: number, total: number) => `¡He acertado ${correct}/${total} banderas! 🎯`,
    try: 'Pruébalo tú:',
    tags: '#Globdrop #Geografía',
    flagTags: '#Globdrop #Banderas #Geografía'
  },
  en: {
    title: '🌍 Globdrop — Can you beat me?',
    flagTitle: '🚩 Globdrop Flags — Can you beat me?',
    score: (pts: string, sc: string, m: string) => `I scored ${pts} points playing ${sc} (${m} mode).`,
    flagScore: (pts: string, sc: string, m: string) => `I scored ${pts} points guessing flags in ${sc} (${m} mode).`,
    level: (lvl: string) => `I became a true ${lvl}!`,
    km: (km: number) => `I traveled ${km} unnecessary km 😅`,
    mi: (mi: number) => `I traveled ${mi} unnecessary miles 😅`,
    flagCorrect: (correct: number, total: number) => `I got ${correct}/${total} flags right! 🎯`,
    try: 'Try it yourself:',
    tags: '#Globdrop #Geography',
    flagTags: '#Globdrop #Flags #Geography'
  },
  zh: {
    title: '🌍 Globdrop — 你能打败我吗？',
    flagTitle: '🚩 Globdrop 国旗 — 你能打败我吗？',
    score: (pts: string, sc: string, m: string) => `我在 ${sc}（${m} 模式）中获得了 ${pts} 分。`,
    flagScore: (pts: string, sc: string, m: string) => `我在 ${sc} 国旗挑战（${m} 模式）中获得了 ${pts} 分。`,
    level: (lvl: string) => `我成为了一个真正的 ${lvl}！`,
    km: (km: number) => `我走了 ${km} 公里多余的路 😅`,
    mi: (mi: number) => `我走了 ${mi} 英里多余的路 😅`,
    flagCorrect: (correct: number, total: number) => `我答对了 ${correct}/${total} 面国旗！ 🎯`,
    try: '你自己试试：',
    tags: '#Globdrop #地理',
    flagTags: '#Globdrop #国旗 #地理'
  },
  ar: {
    title: '🌍 Globdrop — هل يمكنك هزيمتي؟',
    flagTitle: '🚩 Globdrop أعلام — هل يمكنك هزيمتي؟',
    score: (pts: string, sc: string, m: string) => `لقد سجلت ${pts} نقطة في لعب ${sc} (وضع ${m}).`,
    flagScore: (pts: string, sc: string, m: string) => `لقد سجلت ${pts} نقطة في تخمين الأعلام في ${sc} (وضع ${m}).`,
    level: (lvl: string) => `لقد أصبحت ${lvl} حقيقيًا!`,
    km: (km: number) => `لقد قطعت ${km} كم غير ضرورية 😅`,
    mi: (mi: number) => `لقد قطعت ${mi} ميل غير ضروري 😅`,
    flagCorrect: (correct: number, total: number) => `لقد خمنت ${correct}/${total} من الأعلام بشكل صحيح! 🎯`,
    try: 'جربها بنفسك:',
    tags: '#Globdrop #جغرافيا',
    flagTags: '#Globdrop #أعلام #جغرافيا'
  },
  ja: {
    title: '🌍 Globdrop — 私に勝てるかな？',
    flagTitle: '🚩 Globdrop 国旗 — 私に勝てるかな？',
    score: (pts: string, sc: string, m: string) => `${sc}（${m}モード）をプレイして ${pts} 点を獲得しました。`,
    flagScore: (pts: string, sc: string, m: string) => `${sc}国旗クイズ（${m}モード）で ${pts} 点を獲得しました。`,
    level: (lvl: string) => `私は真の ${lvl} になりました！`,
    km: (km: number) => `${km} km 余計に移動してしまいました 😅`,
    mi: (mi: number) => `${mi} マイル余計に移動してしまいました 😅`,
    flagCorrect: (correct: number, total: number) => `${total}枚中 ${correct}枚の国旗を正解しました！ 🎯`,
    try: 'あなたも挑戦してみて：',
    tags: '#Globdrop #地理',
    flagTags: '#Globdrop #国旗 #地理'
  }
};

export function buildShareMessage(data: ShareData): string {
  const lang = getLanguage();
  const m = messages[lang as keyof typeof messages] || messages.en;
  const locale = getLocale();
  
  if (data.isFlags) {
    return [
      m.flagTitle,
      '',
      m.flagScore(data.score.toLocaleString(locale), data.scenario, data.mode),
      m.level(data.level),
      m.flagCorrect(data.correctCount || 0, data.totalRounds || 0),
      '',
      `${m.try} ${data.appUrl || APP_URL}`,
      '',
      m.flagTags,
    ].join('\n');
  }

  return [
    m.title,
    '',
    m.score(data.score.toLocaleString(locale), data.scenario, data.mode),
    m.level(data.level),
    data.distanceUnit === 'mi' ? m.mi(data.kmTotal) : m.km(data.kmTotal),
    '',
    `${m.try} ${data.appUrl || APP_URL}`,
    '',
    m.tags,
  ].join('\n');
}

export async function shareNative(data: ShareData): Promise<boolean> {
  if (!navigator.share) return false;
  const lang = getLanguage();
  const m = messages[lang as keyof typeof messages] || messages.en;
  const title = data.isFlags ? m.flagTitle : m.title;
  try {
    await navigator.share({
      title: title,
      text: buildShareMessage(data),
      url: data.appUrl || APP_URL,
    });
    return true;
  } catch {
    return false;
  }
}

export function shareWhatsApp(data: ShareData): void {
  const msg = encodeURIComponent(buildShareMessage(data));
  window.open(`https://wa.me/?text=${msg}`, '_blank');
}

export function shareFacebook(data: ShareData): void {
  const url = encodeURIComponent(data.appUrl || APP_URL);
  const quote = encodeURIComponent(buildShareMessage(data));
  window.open(
    `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`,
    '_blank'
  );
}

export async function shareInstagram(data: ShareData): Promise<void> {
  await copyToClipboard(data);
}

export async function copyToClipboard(data: ShareData): Promise<void> {
  await navigator.clipboard.writeText(buildShareMessage(data));
}

export function canNativeShare(): boolean {
  return typeof navigator.share === 'function';
}
